import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, merge, switchMap, tap } from 'rxjs';
import { PRIMARY_NAV_ITEMS, SECONDARY_NAV_ITEMS } from './shared/configs/nav-items.config';
import { HeaderComponent } from './shared/components/header/header.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { SiThemeService } from '@siemens/element-ng/theme';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@tran-monorepo/frontend/auth';
import { SiLoadingSpinnerComponent } from '@siemens/element-ng/loading-spinner';
import { NotificationOutletComponent } from '@tran-monorepo/frontend/components';
import { ChatbotBubbleComponent } from './shared/components/custom/chatbot-bubble/chatbot-bubble.component';

@Component({
    selector: 'tran-root',
    imports: [
        RouterOutlet,
        NotificationOutletComponent,
        HeaderComponent,
        FooterComponent,
        ChatbotBubbleComponent,
        SiLoadingSpinnerComponent,
        TranslatePipe
    ],
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
    protected readonly authService = inject(AuthService);
    private readonly router = inject(Router);
    private themeService = inject(SiThemeService);
    private readonly translateService = inject(TranslateService);
    private readonly pageTitleService = inject(Title);
    private readonly destroyRef = inject(DestroyRef);
    private readonly activatedRoute = inject(ActivatedRoute);
    public readonly primaryNavItems = PRIMARY_NAV_ITEMS;
    public readonly secondaryNavItems = SECONDARY_NAV_ITEMS;
    protected readonly isHomePage = signal(false);

    ngOnInit(): void {
        this.themeService.applyThemeType('dark');
        this.trackHomePage();
        this.translatePageTitle();
    }

    protected isRouterPathUnauthorized(): boolean {
        return this.router.url.startsWith('/unauthorized');
    }

    protected isRouterPathNotFound(): boolean {
        return this.router.url.startsWith('/not-found');
    }

    private trackHomePage(): void {
        this.isHomePage.set(this.router.url === '/');
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                tap(event => this.isHomePage.set(event.urlAfterRedirects === '/')),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private translatePageTitle(): void {
        merge(
            this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
            this.translateService.onLangChange
        )
            .pipe(
                map(() => this.deepestRoute()),
                map(route => route.snapshot.data['title'] || 'page-titles.home'),
                switchMap(title => this.translateService.get(title)),
                tap(translatedTitle => {
                    this.pageTitleService.setTitle(translatedTitle);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private deepestRoute(): ActivatedRoute {
        let route = this.activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }
        return route;
    }
}
